import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/**
 * Shared layout for legal pages. Content is plain prose, mobile-first.
 * Replace the draft `sections` arrays with lawyer-reviewed copy later
 * without touching the component itself.
 */
function LegalLayout({ title, lastUpdated, intro, sections, testid }) {
  return (
    <div className="bg-[#F9F8F6]" data-testid={testid}>
      <div className="bg-[#0A192F] text-white">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-10">
          <Link to="/" className="text-xs text-white/60 hover:text-[#F97316] inline-flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> Back to home
          </Link>
          <div className="gj-divider-gold mt-4 mb-3" />
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
            {title}
          </h1>
          {lastUpdated && (
            <p className="mt-2 text-xs text-white/60 uppercase tracking-[0.18em]">
              Last updated: {lastUpdated}
            </p>
          )}
        </div>
      </div>

      <article className="max-w-3xl mx-auto px-4 md:px-8 py-10 md:py-14">
        {intro && (
          <p className="text-[#4A5568] text-sm md:text-base leading-relaxed mb-8">
            {intro}
          </p>
        )}

        {sections.map((s, i) => (
          <section key={s.heading} className="mb-8">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-[#0A192F] mb-3">
              {i + 1}. {s.heading}
            </h2>
            {s.paragraphs?.map((p, idx) => (
              <p
                key={`${s.heading}-p-${idx}`}
                className="text-[#4A5568] text-sm md:text-base leading-relaxed mb-3"
              >
                {p}
              </p>
            ))}
            {s.bullets && (
              <ul className="list-disc pl-5 space-y-1.5 text-[#4A5568] text-sm md:text-base">
                {s.bullets.map((b, idx) => (
                  <li key={`${s.heading}-b-${idx}`}>{b}</li>
                ))}
              </ul>
            )}
          </section>
        ))}

        <div className="mt-12 bg-white border border-[#0A192F]/10 rounded-xl p-5 text-sm">
          <div className="font-heading font-bold text-[#0A192F]">Contact for queries</div>
          <p className="text-[#4A5568] mt-2">
            Gaddi Junction · Kolkata, West Bengal
            <br />
            📧 <a href="mailto:support@gaddijunction.in" className="hover:text-[#0A192F]">support@gaddijunction.in</a>
            <br />
            📞 <a href="tel:+918910884194" className="hover:text-[#0A192F]">+91 89108 84194</a>
          </p>
        </div>
      </article>
    </div>
  );
}

/* -------------------- TERMS & CONDITIONS -------------------- */
export function TermsPage() {
  return (
    <LegalLayout
      testid="legal-terms-page"
      title="Terms & Conditions"
      lastUpdated="February 2026"
      intro="Welcome to Gaddi Junction (“we”, “our”, “us”). By accessing or using gaddijunction.in or any associated mobile or progressive web application, you (“user”, “you”) agree to be bound by these Terms & Conditions. If you do not agree, please do not use the platform."
      sections={[
        {
          heading: "Nature of the Platform",
          paragraphs: [
            "Gaddi Junction is a digital marketplace that connects used-car dealers and individual sellers with prospective buyers across West Bengal and India. We do NOT own, sell, inspect, deliver, finance or insure any vehicle listed on the platform.",
            "All sale transactions occur directly between the buyer and the seller / dealer. Gaddi Junction is not a party to such transactions and has no liability arising therefrom.",
          ],
        },
        {
          heading: "Eligibility",
          bullets: [
            "You must be at least 18 years of age and competent to contract under Indian law.",
            "Dealer accounts must be created by an individual authorized to act on behalf of the dealership.",
            "You must provide accurate, current and complete information at the time of registration and listing.",
          ],
        },
        {
          heading: "User Obligations",
          bullets: [
            "Provide truthful, accurate and verifiable vehicle information including make, model, year, KMs driven, owner history, price, and condition.",
            "Upload genuine photographs of the actual vehicle being listed — stock photos are not allowed.",
            "Maintain the confidentiality of your account credentials and notify us immediately of any unauthorized use.",
            "Comply with all applicable Indian laws including the Motor Vehicles Act, 1988, GST regulations, and consumer protection laws.",
          ],
        },
        {
          heading: "Prohibited Conduct",
          bullets: [
            "Listing stolen, encumbered or vehicles without clear title and valid registration documents.",
            "Uploading misleading information, fake KM readings, or doctored images.",
            "Using the platform to defraud, harass, spam or solicit users.",
            "Reverse-engineering, scraping or abusing the platform's APIs or services.",
          ],
        },
        {
          heading: "Listings, Verification & Removal",
          paragraphs: [
            "All listings posted by dealers undergo a manual admin review before being published. We reserve the right to approve, reject, edit, feature or remove any listing at our sole discretion, without prior notice, including for non-compliance with these Terms.",
            "Admin approval is NOT a certification of the vehicle's condition, title or legality — it is only a basic compliance check.",
          ],
        },
        {
          heading: "Fees",
          paragraphs: [
            "Listing on the platform is currently free for an introductory period. We may introduce subscription plans, featured-listing fees, or commission models in the future with reasonable advance notice on the platform.",
          ],
        },
        {
          heading: "Intellectual Property",
          paragraphs: [
            "All trademarks, logos, branding, design elements, code and content on gaddijunction.in are the property of Gaddi Junction or its licensors. You may not copy, modify, distribute or commercially exploit any part of the platform without prior written consent.",
            "By uploading content (text, photos, documents), you grant Gaddi Junction a non-exclusive, royalty-free, worldwide license to display, host, store and promote that content as part of our service.",
          ],
        },
        {
          heading: "Limitation of Liability",
          paragraphs: [
            "To the maximum extent permitted by law, Gaddi Junction's aggregate liability arising out of or in connection with the platform shall not exceed Rs. 1,000 (Rupees One Thousand only).",
            "Gaddi Junction shall not be liable for any indirect, incidental, consequential, special or punitive damages, including loss of profits, data, goodwill or business opportunity.",
          ],
        },
        {
          heading: "Indemnity",
          paragraphs: [
            "You agree to indemnify and hold harmless Gaddi Junction, its affiliates, directors, employees and agents from any claim, loss, liability, damage or expense arising out of your breach of these Terms, your use of the platform, or any transaction you enter into through the platform.",
          ],
        },
        {
          heading: "Termination",
          paragraphs: [
            "We may suspend or terminate your account at any time, with or without cause or notice, including if we suspect fraudulent activity, breach of these Terms, or risk to the platform or other users.",
          ],
        },
        {
          heading: "Governing Law & Jurisdiction",
          paragraphs: [
            "These Terms are governed by the laws of India. Any dispute arising out of or relating to these Terms or the platform shall be subject to the exclusive jurisdiction of the courts of Kolkata, West Bengal.",
          ],
        },
        {
          heading: "Changes to these Terms",
          paragraphs: [
            "We may update these Terms from time to time. Material changes will be communicated by updating the “Last updated” date above and, where appropriate, via email or in-app notice. Continued use of the platform after changes constitutes acceptance.",
          ],
        },
      ]}
    />
  );
}

/* -------------------- PRIVACY POLICY -------------------- */
export function PrivacyPage() {
  return (
    <LegalLayout
      testid="legal-privacy-page"
      title="Privacy Policy"
      lastUpdated="February 2026"
      intro="This Privacy Policy describes how Gaddi Junction collects, uses, stores and shares your personal information when you use gaddijunction.in. Your privacy matters to us — please read this carefully."
      sections={[
        {
          heading: "Information We Collect",
          bullets: [
            "Account information: name, email, password (hashed), role (customer / dealer / admin).",
            "Dealer onboarding details: dealership name, owner name, mobile, WhatsApp, business address, city, state, business type, stock count.",
            "Optional verification documents: trade license, GST certificate, ID proof, address proof (when uploaded).",
            "Listing data: vehicle details and photos you publish.",
            "Enquiry data: name, phone, email (if provided) and message of buyers who submit an enquiry.",
            "Technical data: IP address, browser user-agent, device type, approximate location, and access timestamps.",
            "Cookies & local storage: a first-party authentication cookie and minimal app-state values (e.g. install-prompt dismissal).",
          ],
        },
        {
          heading: "How We Use Your Information",
          bullets: [
            "To create and operate your account on the platform.",
            "To display listings to potential buyers and connect buyers with dealers via WhatsApp / phone.",
            "To run admin verification, fraud-prevention and security checks.",
            "To send service-related notifications (account alerts, new enquiries) via email, WhatsApp or in-app channels.",
            "To improve the platform's features, performance and security.",
            "To comply with legal obligations and respond to lawful requests by public authorities.",
          ],
        },
        {
          heading: "Sharing of Information",
          paragraphs: [
            "We do NOT sell your personal data. We share limited information only in the following situations:",
          ],
          bullets: [
            "With other users to facilitate a transaction (e.g. a buyer who clicks WhatsApp on your listing sees your business name and phone number).",
            "With service providers under contract (hosting, email/WhatsApp delivery e.g. CallMeBot), strictly for operating the platform.",
            "With law-enforcement or regulators when required by law or to protect rights, property or safety.",
            "In a merger, acquisition or asset sale — in which case the acquiring entity will be bound by this Policy or a successor with substantially similar terms.",
          ],
        },
        {
          heading: "Data Retention",
          paragraphs: [
            "We retain account, listing and enquiry data for as long as your account is active and for a reasonable period afterwards to comply with legal, tax and audit obligations. You may request deletion of your account at any time by writing to support@gaddijunction.in.",
          ],
        },
        {
          heading: "Your Rights",
          bullets: [
            "Access — request a copy of the personal data we hold about you.",
            "Correction — update or correct inaccurate information.",
            "Deletion — request permanent deletion (subject to legal retention requirements).",
            "Withdrawal of consent — withdraw consent for non-essential processing.",
          ],
        },
        {
          heading: "Security",
          paragraphs: [
            "We use reasonable technical and organizational safeguards including HTTPS, hashed passwords, role-based access, and httpOnly authentication cookies. However, no method of transmission or storage is 100% secure, and we cannot guarantee absolute security.",
          ],
        },
        {
          heading: "Children's Privacy",
          paragraphs: [
            "The platform is not intended for users under 18 years of age. We do not knowingly collect data from minors.",
          ],
        },
        {
          heading: "Changes to this Policy",
          paragraphs: [
            "We may update this Policy from time to time. The “Last updated” date will reflect the latest revision. Continued use of the platform constitutes acceptance.",
          ],
        },
      ]}
    />
  );
}

/* -------------------- REFUND & CANCELLATION -------------------- */
export function RefundPage() {
  return (
    <LegalLayout
      testid="legal-refund-page"
      title="Refund & Cancellation Policy"
      lastUpdated="February 2026"
      intro="Gaddi Junction is a digital marketplace connecting buyers and dealers. We do not directly process payments for vehicle sales. This policy describes how cancellations and refunds work for any optional services provided by us."
      sections={[
        {
          heading: "Vehicle Sale Transactions",
          paragraphs: [
            "All payments for vehicles happen directly between buyer and dealer / seller. Gaddi Junction does NOT collect, process, hold or refund vehicle sale payments. Any refund or cancellation arising from a vehicle transaction is to be settled between the parties.",
          ],
        },
        {
          heading: "Dealer Subscriptions & Featured Listings (When Applicable)",
          paragraphs: [
            "If and when we introduce paid subscriptions or featured-listing upgrades, the following will apply unless stated otherwise on the purchase page:",
          ],
          bullets: [
            "Cancellation: you may cancel your subscription at any time from your dealer dashboard. Cancellation stops future renewals.",
            "Refund: amounts paid for the current billing cycle are non-refundable except where required by law or where Gaddi Junction is at fault (e.g., extended platform outage exceeding 48 hours).",
            "Refund request: write to support@gaddijunction.in within 7 days of the disputed charge.",
          ],
        },
        {
          heading: "Refund Processing Time",
          paragraphs: [
            "Approved refunds are processed within 7–14 business days to the original payment method. Bank or processor delays may add up to 5 additional days.",
          ],
        },
        {
          heading: "Listing Removal",
          paragraphs: [
            "You may remove any listing at any time from your dealer dashboard. No refund is due for the unused portion of a featured-listing slot if you self-remove the listing.",
          ],
        },
        {
          heading: "Contact",
          paragraphs: [
            "For all refund or cancellation queries, write to support@gaddijunction.in with your registered email, transaction ID and a brief description of the issue.",
          ],
        },
      ]}
    />
  );
}

/* -------------------- DISCLAIMER -------------------- */
export function DisclaimerPage() {
  return (
    <LegalLayout
      testid="legal-disclaimer-page"
      title="Disclaimer"
      lastUpdated="February 2026"
      intro="Please read this Disclaimer carefully before using Gaddi Junction. By using the platform you acknowledge that you have read, understood and agreed to the matters set out below."
      sections={[
        {
          heading: "Marketplace Only",
          paragraphs: [
            "Gaddi Junction is a digital marketplace platform that allows dealers and individual sellers to list used vehicles and prospective buyers to discover and enquire about those vehicles. We are NOT a seller, importer, agent, broker, financier, insurer or guarantor of any vehicle on the platform.",
          ],
        },
        {
          heading: "What We Are NOT Responsible For",
          paragraphs: [
            "To the fullest extent permitted by law, Gaddi Junction shall not be responsible or liable for:",
          ],
          bullets: [
            "Vehicle condition — mechanical health, accident history, odometer accuracy, service records, or any latent or patent defects.",
            "Payment disputes — including non-payment, delayed payment, refund disputes, cheque bounces, UPI fraud or counterfeit currency.",
            "RC transfer disputes — including delays, refusals, missing documents, RTO complications, hypothecation issues or duplicate / fake RCs.",
            "Fraud by buyers, sellers or third parties — including identity fraud, document forgery, kidnapping of test-drive vehicles, or any criminal acts.",
            "Insurance, fitness certificate, pollution certificate (PUC), tax dues, traffic challans or any liability arising before or after sale.",
            "Loss of profits, business or data resulting from use or inability to use the platform.",
          ],
        },
        {
          heading: "Buyer / Seller Responsibility",
          bullets: [
            "Inspect the vehicle in person (or via qualified mechanic) before any payment.",
            "Verify all documents — RC, insurance, PUC, NOC if applicable, loan-closure certificate, prior owner trail.",
            "Conduct transactions in safe, public locations — preferably at the dealer's registered showroom.",
            "Pay only via traceable channels (bank transfer, UPI, demand draft) — avoid large cash payments.",
            "Get the RC transfer initiated at the relevant RTO within 14 days of purchase.",
          ],
        },
        {
          heading: "Information Accuracy",
          paragraphs: [
            "Listings are created by dealers / sellers. While our admin team conducts basic compliance checks, we cannot independently verify every detail. All information should be treated as starting-point only and verified directly with the seller.",
          ],
        },
        {
          heading: "External Links & Third-Party Services",
          paragraphs: [
            "Gaddi Junction may link to or integrate with third-party services such as WhatsApp, Google Maps, payment processors, and analytics providers. We are not responsible for the content, accuracy, security or practices of these third parties.",
          ],
        },
        {
          heading: "No Warranty",
          paragraphs: [
            "The platform is provided on an “as is” and “as available” basis without warranties of any kind, express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, non-infringement, or uninterrupted availability.",
          ],
        },
        {
          heading: "Acceptance",
          paragraphs: [
            "By using the platform you confirm that you understand and accept this Disclaimer. If you do not agree, please discontinue use immediately.",
          ],
        },
      ]}
    />
  );
}
